import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  FileSpreadsheet,
  GripVertical,
  History,
  Plus,
  RotateCcw,
  Settings2,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { HelpTooltip } from '../components/HelpTooltip'
import { ReferenceUpdateNotice } from '../components/ReferenceUpdateNotice'
import {
  applyCandidateChanges,
  materializeProjectProcess,
  processPhases,
  structureCandidates,
} from '../lib/project'
import type { CandidateChange, ProcessPhase, ProcessStep, ProjectRow, ProjectSnapshot } from '../types'

type Props = {
  project: ProjectRow
  busy: boolean
  onBack: () => void
  onHistory: () => void
  onExport: () => void
  onSave: (snapshot: ProjectSnapshot, summary: string) => Promise<boolean>
  referenceUpdate?: { currentId: string; targetId: string; onOpen: () => void }
}

function nextPhaseId(phases: ProcessPhase[]) {
  let number = phases.length + 1
  while (phases.some((phase) => phase.id === `PH-${String(number).padStart(2, '0')}`)) number += 1
  return `PH-${String(number).padStart(2, '0')}`
}

function nextStepId(phase: ProcessPhase) {
  let number = phase.steps.length + 1
  const prefix = phase.id.replace('PH-', 'SP-')
  while (phase.steps.some((step) => step.id === `${prefix}-${String(number).padStart(2, '0')}`)) number += 1
  return `${prefix}-${String(number).padStart(2, '0')}`
}

function move<T>(items: T[], index: number, direction: -1 | 1) {
  const target = index + direction
  if (target < 0 || target >= items.length) return items
  const next = [...items]
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}

function valueLabel(value: CandidateChange['returned']) {
  if (value === null || value === '') return 'Retiré / vide'
  if (value === true) return 'Oui'
  if (value === false) return 'Non'
  return String(value)
}

export function ProjectRouteEditor({ project, busy, onBack, onHistory, onExport, onSave, referenceUpdate }: Props) {
  const [draft, setDraft] = useState(() => materializeProjectProcess(project.snapshot))
  const [selectedPhaseId, setSelectedPhaseId] = useState(project.current_phase_id)
  const [selectedStepId, setSelectedStepId] = useState(project.current_step_id)
  const [reviewing, setReviewing] = useState(false)
  const [decisions, setDecisions] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setDraft(materializeProjectProcess(project.snapshot))
    setSelectedPhaseId(project.current_phase_id)
    setSelectedStepId(project.current_step_id)
    setReviewing(false)
  }, [project.id, project.version, project.snapshot])

  const phases = processPhases(draft, true)
  const selectedPhase = phases.find((phase) => phase.id === selectedPhaseId) ?? phases[0]
  const selectedStep = selectedPhase?.steps.find((step) => step.id === selectedStepId) ?? selectedPhase?.steps[0]
  const candidates = useMemo(
    () => structureCandidates(project.snapshot, draft, project.snapshot, true),
    [draft, project.snapshot],
  )

  function updatePhases(recipe: (phases: ProcessPhase[]) => ProcessPhase[]) {
    setDraft((current) => {
      const next = materializeProjectProcess(current)
      next.project.process_phases = recipe(processPhases(next, true)).map((phase, phaseIndex) => ({
        ...phase,
        order: phaseIndex + 1,
        steps: phase.steps.map((step, stepIndex) => ({ ...step, order: stepIndex + 1 })),
      }))
      return next
    })
  }

  function updatePhase(id: string, update: Partial<ProcessPhase>) {
    updatePhases((items) => items.map((phase) => phase.id === id ? { ...phase, ...update } : phase))
  }

  function updateStep(id: string, update: Partial<ProcessStep>) {
    updatePhases((items) => items.map((phase) => phase.id === selectedPhase?.id
      ? { ...phase, steps: phase.steps.map((step) => step.id === id ? { ...step, ...update } : step) }
      : phase))
  }

  function addPhase() {
    const id = nextPhaseId(phases)
    const stepId = `${id.replace('PH-', 'SP-')}-01`
    const phase: ProcessPhase = {
      id,
      name: 'Nouvelle phase',
      order: phases.length + 1,
      enabled: true,
      steps: [{ id: stepId, name: 'Première étape', order: 1, enabled: true, truth_state: 'particularite_projet' }],
    }
    updatePhases((items) => [...items, phase])
    setSelectedPhaseId(id)
    setSelectedStepId(stepId)
  }

  function removePhase(phase: ProcessPhase) {
    if (phase.steps.some((step) => step.id === project.current_step_id)) return
    updatePhases((items) => items.filter((item) => item.id !== phase.id))
    const fallback = phases.find((item) => item.id !== phase.id)
    if (fallback) {
      setSelectedPhaseId(fallback.id)
      setSelectedStepId(fallback.steps[0]?.id ?? '')
    }
  }

  function addStep() {
    if (!selectedPhase) return
    const id = nextStepId(selectedPhase)
    updatePhases((items) => items.map((phase) => phase.id === selectedPhase.id ? {
      ...phase,
      steps: [...phase.steps, { id, name: 'Nouvelle étape', order: phase.steps.length + 1, enabled: true, truth_state: 'particularite_projet' }],
    } : phase))
    setSelectedStepId(id)
  }

  function removeStep(step: ProcessStep) {
    if (!selectedPhase || step.id === project.current_step_id || selectedPhase.steps.length === 1) return
    updatePhases((items) => items.map((phase) => phase.id === selectedPhase.id ? { ...phase, steps: phase.steps.filter((item) => item.id !== step.id) } : phase))
    setSelectedStepId(selectedPhase.steps.find((item) => item.id !== step.id)?.id ?? '')
  }

  function toggleDecision(candidate: CandidateChange) {
    setDecisions((current) => ({ ...current, [candidate.id]: !(current[candidate.id] ?? true) }))
  }

  async function publish() {
    const selected = candidates.map((candidate) => ({ ...candidate, accepted: decisions[candidate.id] ?? true }))
    const accepted = selected.filter((candidate) => candidate.accepted)
    if (!accepted.length) return
    const next = applyCandidateChanges(project.snapshot, selected)
    next.project.events.unshift({
      id: crypto.randomUUID(),
      date: next.as_of_date,
      type: 'adaptation_parcours',
      summary: `Parcours du projet adapté · ${accepted.length} changement(s)`,
    })
    const saved = await onSave(next, `Parcours du projet · ${accepted.length} changement(s)`)
    if (saved) setReviewing(false)
  }

  const invalid = !phases.length
    || phases.some((phase) => !phase.name.trim() || !phase.steps.length || phase.steps.some((step) => !step.name.trim()))
    || phases.find((phase) => phase.steps.some((step) => step.id === project.current_step_id))?.enabled === false
    || phases.some((phase) => phase.steps.some((step) => step.id === project.current_step_id && step.enabled === false))

  return (
    <main className="workspace route-editor-view">
      <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> Retour au projet</button>
      <div className="route-editor-title">
        <div><span className="eyebrow">PARCOURS SPÉCIFIQUE AU PROJET</span><h1>Adapter le parcours</h1><p>{project.name} · basé sur {project.snapshot.reference.id}</p></div>
        <div><button className="secondary-button" onClick={onHistory} type="button"><History size={17} /> Historique</button><button className="secondary-button" onClick={onExport} type="button"><FileSpreadsheet size={17} /> Modifier dans Excel</button></div>
      </div>

      <section className="route-truth-banner"><Settings2 size={19} /><span><strong>Vous adaptez uniquement ce projet.</strong> La référence générale reste intacte.</span><HelpTooltip text="Le Cockpit, le Tableau, le Plan 2D et le prochain Excel utiliseront le nouvel ordre une fois les changements publiés." label="Où ces changements seront-ils visibles ?" align="right" /></section>
      {referenceUpdate ? <ReferenceUpdateNotice {...referenceUpdate} /> : null}

      <section className="route-editor-grid">
        <aside className="route-phase-panel">
          <header><strong>Phases du parcours</strong><HelpTooltip text="Sélectionnez une phase pour modifier ses étapes. Les flèches changent son ordre dans tout le projet." label="Comment organiser les phases ?" align="left" /></header>
          <div className="route-phase-list">
            {phases.map((phase, index) => (
              <button className={selectedPhase?.id === phase.id ? 'active' : ''} onClick={() => { setSelectedPhaseId(phase.id); setSelectedStepId(phase.steps[0]?.id ?? '') }} key={phase.id} type="button">
                <GripVertical size={15} /><span className="route-number">{index + 1}</span><span>{phase.name}<small>{phase.steps.length} étape{phase.steps.length > 1 ? 's' : ''}</small></span>
                <i className={phase.enabled === false ? 'off' : ''} title={phase.enabled === false ? 'Phase inactive' : 'Phase active'} />
              </button>
            ))}
          </div>
          <button className="route-add-button" onClick={addPhase} type="button"><Plus size={17} /> Ajouter une phase</button>
          <div className="route-derogation-card">
            <strong>Dérogation <HelpTooltip text="Cette option indique si la branche de dérogation doit être intégrée au parcours de ce projet." label="À quoi sert la branche de dérogation ?" align="left" /></strong>
            <select value={draft.project.derogation_applicability} onChange={(event) => setDraft((current) => ({ ...current, project: { ...current.project, derogation_applicability: event.target.value as ProjectSnapshot['project']['derogation_applicability'] } }))}>
              <option value="a_confirmer">À confirmer</option><option value="oui">Applicable</option><option value="non">Non applicable</option>
            </select>
          </div>
        </aside>

        <section className="route-step-panel">
          <header>
            <div><small>Étapes de la phase</small><input aria-label="Nom de la phase" value={selectedPhase?.name ?? ''} onChange={(event) => selectedPhase && updatePhase(selectedPhase.id, { name: event.target.value })} /></div>
            <div className="route-phase-actions">
              <button disabled={!selectedPhase || selectedPhase.order === 1} onClick={() => selectedPhase && updatePhases((items) => move(items, items.findIndex((item) => item.id === selectedPhase.id), -1))} title="Monter la phase" type="button"><ArrowUp size={16} /></button>
              <button disabled={!selectedPhase || selectedPhase.order === phases.length} onClick={() => selectedPhase && updatePhases((items) => move(items, items.findIndex((item) => item.id === selectedPhase.id), 1))} title="Descendre la phase" type="button"><ArrowDown size={16} /></button>
              <label className="route-switch"><input checked={selectedPhase?.enabled !== false} onChange={(event) => selectedPhase && updatePhase(selectedPhase.id, { enabled: event.target.checked })} type="checkbox" /><span /> Active</label>
              <button className="danger-icon" disabled={!selectedPhase || selectedPhase.steps.some((step) => step.id === project.current_step_id) || phases.length === 1} onClick={() => selectedPhase && removePhase(selectedPhase)} title="Retirer la phase" type="button"><Trash2 size={16} /></button>
            </div>
          </header>
          <div className="route-step-list">
            {selectedPhase?.steps.map((step, index) => (
              <article className={selectedStep?.id === step.id ? 'active' : ''} onClick={() => setSelectedStepId(step.id)} key={step.id}>
                <GripVertical size={17} /><span className="route-number">{selectedPhase.order}.{index + 1}</span><span><strong>{step.name}</strong><small>{step.id}{step.optional ? ' · Facultative' : ''}{step.enabled === false ? ' · Inactive' : ''}</small></span>
                <button disabled={index === 0} onClick={(event) => { event.stopPropagation(); updatePhases((items) => items.map((phase) => phase.id === selectedPhase.id ? { ...phase, steps: move(phase.steps, index, -1) } : phase)) }} title="Monter l’étape" type="button"><ArrowUp size={15} /></button>
                <button disabled={index === selectedPhase.steps.length - 1} onClick={(event) => { event.stopPropagation(); updatePhases((items) => items.map((phase) => phase.id === selectedPhase.id ? { ...phase, steps: move(phase.steps, index, 1) } : phase)) }} title="Descendre l’étape" type="button"><ArrowDown size={15} /></button>
              </article>
            ))}
          </div>
          <button className="route-add-button" onClick={addStep} type="button"><Plus size={17} /> Ajouter une étape</button>
        </section>

        <aside className="route-inspector">
          <header><small>Étape sélectionnée</small><strong>{selectedStep?.id ?? '—'}</strong></header>
          {selectedStep ? <>
            <label>Nom de l’étape<input value={selectedStep.name} onChange={(event) => updateStep(selectedStep.id, { name: event.target.value })} /></label>
            <label>Interlocuteur<input value={selectedStep.actor_default ?? ''} onChange={(event) => updateStep(selectedStep.id, { actor_default: event.target.value })} placeholder="À préciser" /></label>
            <label>Résultat attendu<textarea rows={4} value={selectedStep.expected_output ?? ''} onChange={(event) => updateStep(selectedStep.id, { expected_output: event.target.value })} placeholder="Livrable ou décision attendue" /></label>
            <label className="route-switch wide"><input checked={selectedStep.optional === true} onChange={(event) => updateStep(selectedStep.id, { optional: event.target.checked })} type="checkbox" /><span /> Étape facultative</label>
            <label className="route-switch wide"><input checked={selectedStep.enabled !== false} disabled={selectedStep.id === project.current_step_id} onChange={(event) => updateStep(selectedStep.id, { enabled: event.target.checked })} type="checkbox" /><span /> Étape active</label>
            <button className="text-danger" disabled={selectedStep.id === project.current_step_id || selectedPhase?.steps.length === 1} onClick={() => removeStep(selectedStep)} type="button"><Trash2 size={16} /> Retirer cette étape</button>
          </> : null}
        </aside>
      </section>

      <footer className="route-editor-footer">
        <div><Settings2 size={20} /><span><strong>{candidates.length} modification{candidates.length > 1 ? 's' : ''} non publiée{candidates.length > 1 ? 's' : ''}</strong><small>Le projet ne change qu’après votre validation.</small></span></div>
        <button className="secondary-button" disabled={!candidates.length} onClick={() => setDraft(materializeProjectProcess(project.snapshot))} type="button"><RotateCcw size={16} /> Annuler</button>
        <button className="primary-button" disabled={!candidates.length || invalid} onClick={() => { setDecisions({}); setReviewing(true) }} type="button">Revoir les changements</button>
      </footer>

      {reviewing ? <div className="drawer-backdrop route-review-backdrop" role="presentation" onMouseDown={() => setReviewing(false)}><section className="route-review" role="dialog" aria-modal="true" aria-labelledby="route-review-title" onMouseDown={(event) => event.stopPropagation()}>
        <header><div><span className="eyebrow">AVANT PUBLICATION</span><h2 id="route-review-title">Vérifier les changements du parcours</h2><p>Choisissez les changements à appliquer à {project.name}.</p></div><button className="icon-button" onClick={() => setReviewing(false)} aria-label="Fermer" type="button">×</button></header>
        <div className="route-review-list">{candidates.map((candidate) => {
          const checked = decisions[candidate.id] ?? true
          return <article key={candidate.id}><button className={`decision-check ${checked ? 'accepted' : ''}`} onClick={() => toggleDecision(candidate)} type="button">{checked ? <Check size={16} /> : null}</button><div><strong>{candidate.label}</strong><span><s>{valueLabel(candidate.before)}</s><b>→</b>{valueLabel(candidate.returned)}</span></div></article>
        })}</div>
        <footer><button className="secondary-button" onClick={() => setReviewing(false)} type="button">Retour à l’édition</button><button className="primary-button" disabled={busy || !candidates.some((candidate) => decisions[candidate.id] ?? true)} onClick={() => void publish()} type="button">Publier les changements sélectionnés</button></footer>
      </section></div> : null}
    </main>
  )
}
